import { Plan, updateCheckoutSession } from '@otter-ai/recall-client';
import {
  CheckoutSessionActionType,
  PurchaseCheckoutSessionUpdateEvent,
  trackEvent,
} from '@otter-ai/telemetry';
import { useCheckout } from '@stripe/react-stripe-js';
import { useMutation } from '@tanstack/react-query';
import { useAtom, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { planIsUpdatingAtom, selectedPlanAtom } from '#atoms';

type TogglePlanParams = {
  plan: Plan;
  seatCount: number;
};

export const useTogglePlan = () => {
  const { runServerUpdate, id } = useCheckout();
  const [selectedPlan, setSelectedPlan] = useAtom(selectedPlanAtom);
  const setPlanIsUpdating = useSetAtom(planIsUpdatingAtom);

  const togglePlanMutation = useMutation<
    Plan | undefined,
    Error,
    TogglePlanParams,
    { plan: Plan; previousPlan: Plan } | undefined
  >({
    onMutate: (variables) => {
      if (!selectedPlan) return;
      return { plan: variables.plan, previousPlan: selectedPlan };
    },
    mutationFn: async ({ plan, seatCount }: TogglePlanParams) => {
      if (!selectedPlan || plan.id === selectedPlan.id) {
        return;
      }
      setSelectedPlan(plan);
      await runServerUpdate(() =>
        updateCheckoutSession({
          body: {
            checkout_session_id: id,
            session_params: {
              line_items: [
                {
                  adjustable_quantity: { enabled: true },
                  price: plan.stripe_plan_id ?? '',
                  quantity: seatCount,
                },
              ],
            },
          },
          throwOnError: true,
        }),
      );

      return plan;
    },
    onSuccess: (__, variables) => {
      trackEvent(
        PurchaseCheckoutSessionUpdateEvent({
          Status: 'success',
          CheckoutSessionActionType: CheckoutSessionActionType.ChangePlan,
          StripePriceId: variables.plan.stripe_plan_id ?? '',
        }),
      );
    },
    onError: (error, variables, context) => {
      trackEvent(
        PurchaseCheckoutSessionUpdateEvent({
          Status: 'failure',
          ErrorMessage: error.message,
          CheckoutSessionActionType: CheckoutSessionActionType.ChangePlan,
          StripePriceId: variables.plan.stripe_plan_id ?? '',
        }),
      );
      if (context?.previousPlan) {
        setSelectedPlan(context.previousPlan);
      }
    },
  });

  useEffect(() => {
    setPlanIsUpdating(togglePlanMutation.isPending);
  }, [togglePlanMutation.isPending, setPlanIsUpdating]);

  return togglePlanMutation;
};
